import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import ParrotTable from '../../components/admin/ParrotTable.jsx';
import ParrotDetailsPanel from '../../components/admin/ParrotDetailsPanel.jsx';
import FilterModule from '../../modules/filter/FilterModule.jsx';
import { ModuleContextProvider } from '../../modules/context/ModuleContextProvider.jsx';
import ParrotRevisionHistory from '../../components/admin/ParrotRevisionHistory.jsx';
import ParrotCreationModal from '../../components/admin/ParrotCreationModal.jsx';
import KnowledgeBaseSyncStatusModal from '../../components/KnowledgeBaseSyncStatusModal.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Main parrot management component for admin interface
 */
const ParrotManagement = () => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  // State management
  const [parrots, setParrots] = useState([]);
  const [selectedParrot, setSelectedParrot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    user: '',
    sortBy: 'updated_at',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);
  const [kbStatusModal, setKbStatusModal] = useState({
    isOpen: false,
    parrotId: null,
    parrotName: null
  });

  /**
   * Fetch parrots from API with current filters and pagination
   */
  const fetchParrots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      // Add optional filters
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.user) params.append('user', filters.user);

      const response = await apiRequest(`/api/admin/parrots?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      const parrotsArray = data.parrots || [];
      setParrots(parrotsArray);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || parrotsArray.length,
        totalPages: data.pagination?.totalPages || Math.ceil(parrotsArray.length / prev.limit)
      }));

    } catch (error) {
      console.error('Error fetching parrots:', error);
      setError(error.message || 'Failed to load parrots');
      showToast('Failed to load parrots', 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, filters, pagination.page, pagination.limit, showToast]);

  /**
   * Handle filter changes from FilterModule
   */
  const handleFiltersChange = useCallback((activeFilters) => {
    // Convert FilterModule activeFilters to legacy filter format
    const newFilters = {
      search: '',
      status: 'all',
      user: '',
      sortBy: 'updated_at',
      sortOrder: 'desc'
    };

    activeFilters.forEach(filter => {
      switch (filter.id) {
        case 'name':
          newFilters.search = filter.value;
          break;
        case 'status':
          newFilters.status = filter.value;
          break;
        case 'owner_email':
          // Map owner_email to user filter
          newFilters.user = filter.value;
          break;
        default:
          break;
      }
    });

    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  /**
   * Handle legacy filter changes (for backward compatibility)
   */
  const handleLegacyFiltersChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  /**
   * Handle pagination changes
   */
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  /**
   * Handle parrot selection
   */
  const handleParrotSelect = useCallback((parrot) => {
    setSelectedParrot(parrot);
  }, []);

  /**
   * Handle parrot updates
   */
  const handleParrotUpdate = useCallback((updatedParrot) => {
    if (updatedParrot === null) {
      // Handle deletion - remove parrot from list
      setParrots(prev => prev.filter(parrot => parrot.id !== selectedParrot.id));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      setSelectedParrot(null);
      // Toast is handled by the delete function
    } else {
      // Handle update - update parrot in list
      setParrots(prev => prev.map(parrot => 
        parrot.id === updatedParrot.id ? updatedParrot : parrot
      ));
      setSelectedParrot(updatedParrot);
      showToast('Parrot updated successfully', 'success');
    }
  }, [showToast, selectedParrot]);

  /**
   * Handle parrot creation
   */
  const handleParrotCreated = useCallback((newParrot) => {
    setParrots(prev => [newParrot, ...prev]);
    setPagination(prev => ({ ...prev, total: prev.total + 1 }));
    showToast('Parrot created successfully', 'success');
  }, [showToast]);

  /**
   * Open revision history modal
   */
  const openRevisionHistory = useCallback((parrot) => {
    setSelectedParrot(parrot);
    setShowRevisionHistory(true);
  }, []);

  
  // Load parrots on component mount and when dependencies change
  useEffect(() => {
    fetchParrots();
  }, [fetchParrots]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Parrot Management</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
            Manage parrot configurations across all users
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2 dark:text-white"
          >
            <span>+</span>
            <span>Create Parrot</span>
          </button>
          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {pagination.total > 0 && (
              <span>
                Showing {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} parrots
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className={`${theme === 'dark' ? 'bg-red-900/20 border-red-500/50' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
          <div className="flex items-center">
            <span className={`${theme === 'dark' ? 'text-red-400' : 'text-red-600'} mr-2`}>⚠️</span>
            <span className={`${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>{error}</span>
            <button
              onClick={fetchParrots}
              className={`ml-auto ${theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'} underline`}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters sidebar */}
        <div className="lg:col-span-1">
          <ModuleContextProvider
            user={{ id: 'admin', permissions: ['admin'] }}
            permissions={[{ id: 'admin', name: 'Admin Access' }]}
            apiClient={{ get: () => {}, post: () => {}, put: () => {}, delete: () => {} }}
            theme={{ mode: theme }}
            toast={{ success: showToast, error: showToast, warning: showToast, info: showToast }}
          >
            <FilterModule
              config={{
                context: 'admin-parrots',
                filters: [], // Will use context-specific defaults
                layout: 'vertical',
                persistFilters: true,
                storageKey: 'admin-parrots-filters',
                showClearAll: true,
                realTimeFiltering: true,
                debounceMs: 500,
                onFilterChange: handleFiltersChange,
                onFilterReset: () => handleLegacyFiltersChange({
                  search: '',
                  status: 'all',
                  user: '',
                  sortBy: 'updated_at',
                  sortOrder: 'desc'
                })
              }}
              className="h-fit"
              testId="admin-parrots-filter"
            />
          </ModuleContextProvider>
        </div>

        {/* Main content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Parrots table */}
          <ParrotTable
            parrots={parrots}
            loading={loading}
            selectedParrot={selectedParrot}
            onParrotSelect={handleParrotSelect}
            pagination={pagination}
            onPageChange={handlePageChange}
            onSort={(sortBy, sortOrder) => handleLegacyFiltersChange({ sortBy, sortOrder })}
            onViewRevisions={openRevisionHistory}
            onViewKBStatus={(parrot) => {
              setKbStatusModal({
                isOpen: true,
                parrotId: parrot.id,
                parrotName: parrot.name
              });
            }}
          />

          {/* Parrot details panel */}
          {selectedParrot && !showRevisionHistory && (
            <ParrotDetailsPanel
              parrot={selectedParrot}
              onUpdate={handleParrotUpdate}
              onViewRevisions={openRevisionHistory}
              onClose={() => setSelectedParrot(null)}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <ParrotCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onParrotCreated={handleParrotCreated}
      />

      {showRevisionHistory && selectedParrot && (
        <div className={`fixed inset-0 ${theme === 'dark' ? 'bg-black' : 'bg-gray-500'} bg-opacity-50 flex items-center justify-center z-50 p-4`}>
          <div className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <ParrotRevisionHistory
              parrot={selectedParrot}
              onClose={() => {
                setShowRevisionHistory(false);
              }}
              onRevert={handleParrotUpdate}
            />
          </div>
        </div>
      )}

      {/* Knowledge Base Sync Status Modal */}
      <KnowledgeBaseSyncStatusModal
        isOpen={kbStatusModal.isOpen}
        onClose={() => setKbStatusModal({ isOpen: false, parrotId: null, parrotName: null })}
        parrotId={kbStatusModal.parrotId}
        parrotName={kbStatusModal.parrotName}
      />
    </div>
  );
};

export default ParrotManagement;
